import { useRef } from "react";
import { Camera, X, ImageIcon, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { validateImageFiles } from "@/lib/validation";
import { useObjectUrl } from "@/lib/object-url";
import { toast } from "sonner";

interface Props {
  photo: File | null;
  onPhotoChange: (photo: File | null) => void;
  label?: string;
  icon?: "image" | "document";
  captureLabel?: string;
  helperText?: string;
}

export function YardPhotoCapture({ photo, onPhotoChange, label = "Trailer Photo", icon = "image", captureLabel, helperText }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewUrl = useObjectUrl(photo);

  const handleCapture = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const { valid, errors } = validateImageFiles(files);
    if (errors.length > 0) {
      errors.forEach((err) => toast.error(err));
    }
    if (valid.length > 0) {
      onPhotoChange(valid[0]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const IconComponent = icon === "document" ? FileText : ImageIcon;
  const buttonLabel = captureLabel ?? (photo ? "Retake Trailer Photo" : "Take Trailer Photo");

  return (
    <Card className="p-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium flex items-center gap-1.5">
          <IconComponent className="h-4 w-4" />
          {label}
        </label>
        {photo && (
          <button
            onClick={() => onPhotoChange(null)}
            className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"
          >
            <X className="h-3 w-3" />
            Remove
          </button>
        )}
      </div>
      {helperText && (
        <p className="text-xs text-muted-foreground mb-2">{helperText}</p>
      )}

      {photo && previewUrl && (
        <div className="mb-3">
          <img
            src={previewUrl}
            alt={`Pending ${label.toLowerCase()}`}
            decoding="async"
            loading="lazy"
            className="w-full max-h-64 rounded-md object-contain border bg-muted"
          />
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={handleCapture}
        className="w-full"
      >
        <Camera className="h-4 w-4 mr-2" />
        {buttonLabel}
      </Button>
    </Card>
  );
}
